/**
 * Shared rules for every address block in the fact find (address history,
 * property addresses on the Assets step, and anywhere else we capture a
 * structured address).
 *
 * Australian addresses are captured as split pieces (unit / street number /
 * street name / suburb / state / postcode) because that is what lenders want
 * and because Google Places reliably returns all of them here.
 *
 * Overseas addresses are not. Street numbering, states / provinces and
 * postcodes are not universal, so Google returns partial component sets and
 * the customer gets stuck on required fields they cannot fill. For those we
 * keep only what Google gives us and treat the picked address itself as the
 * source of truth.
 */

export const isAUAddress = (country) => (country || 'au').toLowerCase() === 'au';

// The combined single-line `street` value kept for back-compat consumers
// (review screen, print view, summaries, the v1 export shape).
export const combinedStreet = (addr) => {
  const a = addr || {};
  if (!isAUAddress(a.country)) {
    return a.address_search || a.street || '';
  }
  const unit = a.unit_number ? `Unit ${a.unit_number}` : '';
  const numberPlusName = [a.street_number, a.street_name].filter(Boolean).join(' ');
  return [unit, numberPlusName].filter(Boolean).join(', ');
};

// Hint shown under the "Search address" box.
export const addressSearchHint = (country) =>
  isAUAddress(country)
    ? "Start typing and we'll fill in the fields below. You can also edit each piece manually."
    : "Start typing and pick your address. We'll use the full address exactly as it comes back, so there's nothing else to fill in above the city.";
